import { useEffect, useState } from "react";
import {
  ArrowLeft,
  Calendar,
  Clock,
  ExternalLink,
  FileText,
  Share2,
  Target,
  User,
  Video,
} from "lucide-react";
import {
  Link,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

import { getUserAuthTokenForMeeting } from "../../data/appointments";
import { resourcesShared } from "../../data/resources";

const menteeProfile = {
  name: "Genevieve Chen",
  avatar:
    "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150&h=150&fit=crop&crop=face",
  initials: "GC",
  role: "Sales Representative",
  company: "TechCo Inc.",
  email: "genevieve.chen@techco.com",
  phone: "+1 (555) 123-4567",
  experience: "2 years in B2B SaaS sales",
  goals: "Improve objection handling and increase close rate from 15% to 25%",
};

const sessionDetails = {
  date: "December 15, 2024",
  time: "2:00 PM - 2:45 PM PST",
  duration: "45 minutes",
  type: "Sales Strategy Review",
  lastMeeting: "December 8, 2024",
};

const keyInsights = [
  "Struggles with price objections - needs framework for value articulation",
  "Strong rapport building skills but rushes through discovery",
  "Excellent product knowledge but lacks confidence in complex scenarios",
  "Improved significantly in follow-up consistency since last session",
];

const conversationSummary =
  "Genevieve is a motivated sales rep with solid fundamentals who joined our coaching program to break through a performance plateau. She's been working specifically on objection handling techniques and has shown marked improvement in her discovery questioning. Her main challenge is maintaining confidence when prospects push back on pricing, often conceding too quickly rather than reinforcing value. Recent focus has been on developing a structured approach to price objections and building a personal toolkit of compelling value stories.";

export function PreSession() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("sessionId");
  const menteeName = searchParams.get("menteeName");

  const { appointment } = useLocation().state || {};

  const [authToken, setAuthToken] = useState("");

  useEffect(() => {
    if (sessionId) {
      getUserAuthTokenForMeeting(sessionId, "mentor").then((authToken) => {
        setAuthToken(authToken);
      });

      // getLastThreePastAppointments().then((appointments) => {
      //   setPastAppointments(appointments);
      // });
    }
  }, [sessionId, menteeName]);

  return (
    <div className="mx-auto w-full max-w-4xl space-y-6">
      <div className="flex items-center justify-between">
        <Button
          variant="outline"
          onClick={() => navigate("/mentor/appointments")}
          className="flex items-center gap-2 border-primary/20 hover:bg-primary/5"
        >
          <ArrowLeft className="size-4" />
          Back to Sessions
        </Button>
        <Link
          to={`/mentor/video-call?sessionId=${sessionId}&authToken=${authToken}`}
          state={{ appointment }}
        >
          <Button
            className="flex items-center gap-2 border-0 text-white shadow-lg"
            style={{
              background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
            }}
          >
            <Video className="size-4" />
            Start Session
          </Button>
        </Link>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Left Column - Profile & Session Details */}
        <div className="space-y-6 lg:col-span-1">
          {/* Mentee Profile */}
          <Card className="bg-gradient-card border-0 shadow-xl">
            <CardHeader className="border-b border-primary/20 bg-gradient-to-r from-primary/5 to-primary/10">
              <CardTitle className="flex items-center gap-2 text-primary">
                <User className="size-5" />
                Mentee Profile
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 p-6">
              <div className="flex items-center gap-3">
                <Avatar className="size-16 ring-2 ring-primary/20">
                  <AvatarImage
                    src={menteeProfile.avatar}
                    alt={menteeProfile.name}
                  />
                  <AvatarFallback className="bg-gradient-primary text-white">
                    {menteeProfile.initials}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <h3 className="font-semibold text-foreground">
                    {menteeProfile.name}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {menteeProfile.role}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {menteeProfile.company}
                  </p>
                </div>
              </div>

              <div className="space-y-3 text-sm">
                <div className="rounded-lg bg-white/60 p-3">
                  <span className="font-medium text-foreground">Email: </span>
                  <span className="text-muted-foreground">
                    {menteeProfile.email}
                  </span>
                </div>
                <div className="rounded-lg bg-white/60 p-3">
                  <span className="font-medium text-foreground">Phone: </span>
                  <span className="text-muted-foreground">
                    {menteeProfile.phone}
                  </span>
                </div>
                <div className="rounded-lg bg-white/60 p-3">
                  <span className="font-medium text-foreground">
                    Experience:{" "}
                  </span>
                  <span className="text-muted-foreground">
                    {menteeProfile.experience}
                  </span>
                </div>
              </div>

              <div className="rounded-lg border border-primary/10 bg-gradient-to-r from-blue-50 to-indigo-50 p-4">
                <h4 className="mb-2 flex items-center gap-2 font-medium text-foreground">
                  <Target className="size-4 text-primary" />
                  Current Goals
                </h4>
                <p className="text-sm text-muted-foreground">
                  {menteeProfile.goals}
                </p>
              </div>

              <Button
                variant="outline"
                className="flex w-full items-center gap-2 border-primary/20 hover:bg-primary/5"
              >
                <ExternalLink className="size-4" />
                View Full Profile
              </Button>
            </CardContent>
          </Card>

          {/* Session Details */}
          <Card className="bg-gradient-card border-0 shadow-xl">
            <CardHeader className="border-b border-primary/20 bg-gradient-to-r from-primary/5 to-primary/10">
              <CardTitle className="flex items-center gap-2 text-primary">
                <Calendar className="size-5" />
                Session Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 p-6">
              <div className="space-y-3">
                <div className="flex items-center gap-3 rounded-lg bg-white/60 p-3">
                  <Calendar className="size-4 text-primary" />
                  <span className="text-sm text-foreground">
                    {sessionDetails.date}
                  </span>
                </div>
                <div className="flex items-center gap-3 rounded-lg bg-white/60 p-3">
                  <Clock className="size-4 text-primary" />
                  <span className="text-sm text-foreground">
                    {sessionDetails.time}
                  </span>
                </div>
              </div>
              <div className="pt-2">
                <Badge className="bg-gradient-primary border-0 text-white">
                  {sessionDetails.type}
                </Badge>
              </div>
              <div className="border-t border-primary/10 pt-2">
                <div className="rounded-lg bg-gradient-to-r from-green-50 to-emerald-50 p-3">
                  <p className="text-sm">
                    <span className="font-medium text-foreground">
                      Last Meeting:{" "}
                    </span>
                    <span className="text-muted-foreground">
                      {sessionDetails.lastMeeting}
                    </span>
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Insights, Summary & Resources */}
        <div className="space-y-6 lg:col-span-2">
          {/* Key Insights */}
          <Card className="bg-gradient-card border-0 shadow-xl">
            <CardHeader className="border-b border-primary/20 bg-gradient-to-r from-primary/5 to-primary/10">
              <CardTitle className="text-primary">
                Insights About Genevieve
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <ul className="space-y-4">
                {keyInsights.map((insight, index) => (
                  <li
                    key={index}
                    className="flex items-start gap-3 rounded-lg bg-white/60 p-3"
                  >
                    <div className="bg-gradient-primary mt-2 size-2 shrink-0 rounded-full"></div>
                    <p className="text-sm text-foreground">{insight}</p>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>

          {/* Conversation Summary */}
          <Card className="bg-gradient-card border-0 shadow-xl">
            <CardHeader className="border-b border-primary/20 bg-gradient-to-r from-primary/5 to-primary/10">
              <CardTitle className="text-primary">
                Conversation Summary
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="rounded-lg bg-white/60 p-4">
                <p className="text-sm leading-relaxed text-foreground">
                  {conversationSummary}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Resources Shared */}
          <Card className="bg-gradient-card border-0 shadow-xl">
            <CardHeader className="border-b border-primary/20 bg-gradient-to-r from-primary/5 to-primary/10">
              <CardTitle className="flex items-center gap-2 text-primary">
                <Share2 className="size-5" />
                Resources Shared
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="space-y-4">
                {resourcesShared.map((resource, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between rounded-lg border border-primary/10 bg-white/60 p-4 transition-all duration-200 hover:shadow-md"
                  >
                    <div className="flex items-center gap-3">
                      <div className="bg-gradient-primary flex size-10 items-center justify-center rounded-lg">
                        <FileText className="size-5 text-white" />
                      </div>
                      <div>
                        <h4 className="font-medium text-foreground">
                          {resource.title}
                        </h4>
                        <p className="text-sm text-muted-foreground">
                          {resource.description}
                        </p>
                        <div className="mt-1 flex items-center gap-3">
                          <Badge variant="outline" className="text-xs">
                            {resource.type}
                          </Badge>
                          <span className="text-xs text-muted-foreground">
                            Shared: {resource.date}
                          </span>
                        </div>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-primary/20 hover:bg-primary/5"
                    >
                      <ExternalLink className="size-3" />
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
